/**
 * Connected accounts — sign-in providers and integrations linked to the
 * account.
 *
 * What is real here:
 *  - Google: derived from `useAuth().user.provider` (the
 *    `sign_in_provider` Firebase reports for the active session).
 *
 * What is honestly disabled:
 *  - Facebook / Instagram / YouTube tiles render as "Coming soon" and are
 *    not interactive. They previously rendered as Connected/Disconnect
 *    buttons with no backing OAuth flow, which was misleading.
 *
 * To wire a new provider: add a backend OAuth route + token storage, then
 * include the provider id in `useAuth().user.provider` (or a future
 * `connectedProviders` array) and flip the tile to interactive here.
 */
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Icons } from '@/src/modules/components/Icons'
import { cn } from '@/src/infrastructure/lib/utils'
import { SettingsCard, Pill } from '@/src/modules/components/settings/primitives'
import { useAuth } from '@/src/modules/auth/AuthProvider'

type ProviderDef = {
  id: 'google' | 'facebook' | 'instagram' | 'youtube'
  name: string
  description: string
  icon: any
  color: string
  /** Firebase `sign_in_provider` value(s) that mean "connected". */
  firebaseProviderIds?: string[]
}

const PROVIDERS: ProviderDef[] = [
  {
    id: 'google',
    name: 'Google',
    description: 'Sign in with your Google account.',
    icon: Icons.Mail,
    color: '#ea4335',
    firebaseProviderIds: ['google.com'],
  },
  {
    id: 'facebook',
    name: 'Facebook',
    description: 'Share invitations to your timeline.',
    icon: Icons.Users,
    color: '#1877f2',
  },
  {
    id: 'instagram',
    name: 'Instagram',
    description: 'Import photos for your galleries.',
    icon: Icons.Camera,
    color: '#e1306c',
  },
  {
    id: 'youtube',
    name: 'YouTube',
    description: 'Embed pre-wedding films and reels.',
    icon: Icons.ExternalLink,
    color: '#ff0000',
  },
]

export function ConnectedAccountsSection() {
  const { t } = useTranslation('settings')
  const { user } = useAuth()
  const activeProvider = user?.provider ?? null

  return (
    <SettingsCard
      icon={Icons.Plug}
      title={t('connected.title')}
      description={t('connected.description')}
    >
      <div className="grid sm:grid-cols-2 gap-4">
        {PROVIDERS.map((p) => {
          const supported = !!p.firebaseProviderIds
          const connected =
            supported &&
            !!activeProvider &&
            p.firebaseProviderIds!.includes(activeProvider)
          const handle = connected ? user?.email : undefined

          return (
            <div
              key={p.id}
              aria-disabled={!supported}
              className={cn(
                'flex items-center gap-3 rounded-2xl border p-4 transition-colors',
                'border-outline-variant/30',
                supported ? 'bg-surface-low/50' : 'bg-surface-lowest opacity-60',
              )}
            >
              <span
                className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center text-white"
                style={{ background: p.color }}
              >
                <p.icon size={18} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-on-surface">{p.name}</p>
                  {connected && <Pill tone="success">{t('connected.connected')}</Pill>}
                  {!supported && <Pill tone="neutral">{t('connected.comingSoon')}</Pill>}
                </div>
                <p className="text-xs text-on-surface-variant truncate">
                  {handle ?? p.description}
                </p>
              </div>
              {/* No action buttons for unsupported providers — anything else
                  would be a lie about what the app can actually do. */}
              {supported && !connected && (
                <span className="text-xs font-semibold text-on-surface-variant shrink-0">
                  {t('connected.notConnected')}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </SettingsCard>
  )
}
